/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { EventDispatcher, EventObject } from '../events';
import Library from '../library';
import { Logger } from '../logger';
import { GlobalFactStore } from '../rules/facts/store';

import type { Facts } from '../rules';
import type {
  BaseEventAnalyticsData,
  GenericEventAnalyticsData,
  ImpressionAnalyticsData,
  KeyValue,
} from '../types';
import type { UserConsent } from '../types/prebid';
import { debugPixel } from '../util';
import type { ReportingEvents } from './events';

export interface AnalyticsConfig {
  enabled?: boolean;
  recordBids?: boolean;
  endpoint?: {
    origin: string;
  };
}

export interface AnalyticsOptions {
  config: AnalyticsConfig;
  propertyId: string;
}

const enum RequestType {
  GET = 'GET',
  POST = 'POST',
}

const BASE_PATH = '/d/v1';
const OPPORTUNITY_ENDPOINT = '/opp';
const IMPRESSION_ENDPOINT = '/imp';
const AD_INTERACTION_ENDPOINT = '/int';
const VIEWABLE_IMP_ENDPOINT = '/vi';
const FILTERED_SELECTION_REPORT_ENDPOINT = '/ckv';
const PAGE_VIEW_ENDPOINT = '/pv';
const VIDEO_QUARTILE_ENDPOINT = '/vq';
const BRAND_SAFETY_VIOLATION_ENDPOINT = '/bv';
const logger = Logger.getInstance();

enum EventType {
  IMPRESSION = 'impression',
  OPPORTUNITY = 'opportunity',
  AD_INTERACTION = 'interaction',
  VIEWABLE_IMPRESSION = 'viewableImpression',
  FILTERED_SELECTION_REPORT = 'filteredSelectionReport',
  PAGE_VIEW = 'pageView',
  VIDEO_QUARTILE = 'videoQuartile',
  BRAND_SAFETY_VIOLATION = 'brandSafetyViolation',
}

class AnalyticsRecorder {
  private config: AnalyticsConfig;
  private enabled: boolean = true;
  private propertyId: string;
  private auctionNumber: number = 0;
  private eventDispatcher: EventDispatcher<ReportingEvents>;

  constructor(eventDispatcher: EventDispatcher<ReportingEvents>, options: AnalyticsOptions) {
    this.eventDispatcher = eventDispatcher;
    this.config = options.config;
    this.propertyId = options.propertyId;

    if (this.config.enabled !== undefined) {
      this.enabled = Boolean(this.config.enabled);
    }

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    if (!this.enabled) {
      logger.warn('Analytics recording is disabled - no event listeners will be registered');
      return;
    }

    this.eventDispatcher.on('AdImpression', (event) => this.handleAdImpression(event));
    this.eventDispatcher.on('AdViewableImpression', (event) =>
      this.handleAdViewableImpression(event)
    );
    this.eventDispatcher.on('AdInteraction', (event) => this.handleAdInteraction(event));
    this.eventDispatcher.on('AuctionEnd', (event) => this.handleAuctionEnd(event));
    this.eventDispatcher.on('TargetingSelectionComplete', (event) =>
      this.handleTargetingSelectionComplete(event)
    );
    this.eventDispatcher.on('PageView', (event) => this.handlePageView(event));
    this.eventDispatcher.on('VideoQuartile', (event) => this.handleVideoQuartile(event));
    this.eventDispatcher.on('BrandSafetyViolation', (event) =>
      this.handleBrandSafetyViolation(event)
    );
  }

  private getEndpointUrl(eventType: EventType): string {
    const origin: string = this.config!.endpoint!.origin;
    const baseUrl = `${origin}${BASE_PATH}`;
    switch (eventType) {
      case EventType.OPPORTUNITY:
        return `${baseUrl}${OPPORTUNITY_ENDPOINT}`;
      case EventType.IMPRESSION:
        return `${baseUrl}${IMPRESSION_ENDPOINT}`;
      case EventType.AD_INTERACTION:
        return `${baseUrl}${AD_INTERACTION_ENDPOINT}`;
      case EventType.VIEWABLE_IMPRESSION:
        return `${baseUrl}${VIEWABLE_IMP_ENDPOINT}`;
      case EventType.FILTERED_SELECTION_REPORT:
        return `${baseUrl}${FILTERED_SELECTION_REPORT_ENDPOINT}`;
      case EventType.PAGE_VIEW:
        return `${baseUrl}${PAGE_VIEW_ENDPOINT}`;
      case EventType.VIDEO_QUARTILE:
        return `${baseUrl}${VIDEO_QUARTILE_ENDPOINT}`;
      case EventType.BRAND_SAFETY_VIOLATION:
        return `${baseUrl}${BRAND_SAFETY_VIOLATION_ENDPOINT}`;
    }
  }

  private getInitialQueryParamsMap(
    userConsent: UserConsent,
    requestType: RequestType
  ): Record<string, string> {
    let params: Record<string, string> = {
      ...this.generateConsentParamsMap(userConsent),
      rnd: String(Math.random()),
    };
    if (requestType === RequestType.GET) {
      params.dl = `${Library.name}:${Library.version}`;
    }
    return params;
  }

  private handleAdImpression(event: EventObject<ReportingEvents['AdImpression']>): void {
    const { userConsent, ...data } = event.data;

    logger.info(
      `Logging impression for opportunity: ${data.opportunityId} ;impression: ${data.id}`
    );
    const endpointUrl: string = this.getEndpointUrl(EventType.IMPRESSION);
    const parameterMap = this.buildImpressionParameters({ ...data, userConsent });

    const querystring: string = new URLSearchParams(
      Object.entries(parameterMap).filter(([_, value]) => value !== null && value !== undefined)
    ).toString();

    const debugPayload: Record<string, any> = {
      en: data.lineItemId ? 'directImpressionLogged' : 'programmaticImpressionLogged',
      adUnitCode: data.adUnit,
    };
    if (data.lineItemId) {
      debugPayload.advertiserId = data.advertiserId;
    }
    debugPixel(debugPayload);
    this.makeGetRequest(`${endpointUrl}?${querystring}`);
  }

  private handleAdViewableImpression(
    event: EventObject<ReportingEvents['AdViewableImpression']>
  ): void {
    this.handleGenericEvent(
      event.data,
      EventType.VIEWABLE_IMPRESSION,
      `Logging viewable impression for opportunity: ${event.data.opportunityId} ;impression: ${event.data.impressionId}`,
      (data) => this.buildGenericEventParameters(data),
      event.data.useHttpPost ? 'POST' : 'GET'
    );
  }

  private handleAdInteraction(event: EventObject<ReportingEvents['AdInteraction']>): void {
    this.handleGenericEvent(
      event.data,
      EventType.AD_INTERACTION,
      `Logging interaction for opportunity: ${event.data.opportunityId} ;impression: ${event.data.impressionId}`,
      (data) => this.buildGenericEventParameters(data),
      event.data.useHttpPost ? 'POST' : 'GET'
    );
  }

  private handleAuctionEnd(event: EventObject<ReportingEvents['AuctionEnd']>): void {
    const { opportunityId, placements, bids, userConsent, pvid, sid, uid } = event.data;

    // Record opportunity with bid data
    logger.info(`Logging opportunity for id: ${opportunityId}`);
    this.auctionNumber++;
    const opportunityEndpointUrl: string = this.getEndpointUrl(EventType.OPPORTUNITY);
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.POST
    );
    const opportunityPayload: Record<string, any> = this.generateOpportunityPayload(pvid, sid, uid);
    opportunityPayload.id = opportunityId;

    for (const placement of placements) {
      const placementData: any = {
        gad: placement.adUnitCode,
        v: placement.visibility,
        ss: placement.supportedSizes
          .filter((obj: any) => obj != null)
          .map((obj: any) => ({
            w: obj.width,
            h: obj.height,
          })),
      };

      if (this.config.recordBids === true) {
        placementData.bds = bids[placement.adUnitCode]?.map((bid: any) => ({
          ptr: bid.bidder,
          advd: bid?.advertiserDomains,
          bdp: bid.cpm,
          mtpe: bid.mediaType,
          dlid: bid?.dealId,
        }));
      }

      opportunityPayload.plcmts.push(placementData);
    }

    const querystring: string = new URLSearchParams(parameterMap).toString();
    this.makePostRequest(`${opportunityEndpointUrl}?${querystring}`, opportunityPayload);
  }

  private handleTargetingSelectionComplete(
    event: EventObject<ReportingEvents['TargetingSelectionComplete']>
  ): void {
    const { opportunityId, selectionData, userConsent } = event.data;

    // Record filtered selection data
    logger.info(`Logging filtered selection reporting for opportunity: ${opportunityId}`);
    const selectionEndpointUrl: string = this.getEndpointUrl(EventType.FILTERED_SELECTION_REPORT);
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.POST
    );
    const selectionPayload: Record<string, any> = {
      id: opportunityId,
      plcmts: selectionData,
      dl: `${Library.name}:${Library.version}`,
    };

    const querystring: string = new URLSearchParams(parameterMap).toString();
    this.makePostRequest(`${selectionEndpointUrl}?${querystring}`, selectionPayload);
  }

  private handlePageView(event: EventObject<ReportingEvents['PageView']>): void {
    const { id, sid, uid, userConsent } = event.data;

    // Record page view data
    logger.info(`Logging page view: ${id}`);
    const pageViewEndpointUrl: string = this.getEndpointUrl(EventType.PAGE_VIEW);
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.POST
    );
    const pageViewPayload: Record<string, any> = this.generatePageViewPayload(id, sid, uid);

    const querystring: string = new URLSearchParams(parameterMap).toString();
    this.makePostRequest(`${pageViewEndpointUrl}?${querystring}`, pageViewPayload);
  }

  private handleVideoQuartile(event: EventObject<ReportingEvents['VideoQuartile']>): void {
    const { quartile } = event.data;
    this.handleGenericEvent(
      event.data,
      EventType.VIDEO_QUARTILE,
      `Logging video quartile ${quartile} for opportunity: ${event.data.opportunityId} ;impression: ${event.data.impressionId}`,
      (data) => this.buildGenericEventParameters(data, { q: String(quartile) }),
      event.data.useHttpPost ? 'POST' : 'GET'
    );
  }

  private handleBrandSafetyViolation(
    event: EventObject<ReportingEvents['BrandSafetyViolation']>
  ): void {
    const {
      cid,
      pvid,
      sid,
      adUnit,
      detectedWords,
      //@ts-ignore
      userConsent,
      //@ts-ignore
      uid,
      impressionId,
      bidStage,
      opportunityId,
    } = event.data;

    logger.info(`Logging brand safety violation for campaign ${cid} on adUnit ${adUnit}`);

    const payload = {
      en: 'bsv',
      cid: cid,
      pvid: pvid,
      sid: sid,
      //uid: uid || '',
      gad: adUnit,
      dwds: this.base64Encode(detectedWords.sort().join(',')),
      id: impressionId || '',
      bidStage: bidStage,
      rid: opportunityId,
    };

    // Temporary solution: use debugPixel until backend is ready
    debugPixel(payload, true);

    // TODO: Uncomment when backend is ready
    // const endpointUrl: string = this.getEndpointUrl(EventType.BRAND_SAFETY_VIOLATION);
    // const queryParams = this.getInitialQueryParamsMap(userConsent, RequestType.GET);
    // const querystring: string = new URLSearchParams(queryParams).toString();
    // this.makePostRequest(`${endpointUrl}?${querystring}`, payload);
  }

  private serializeKeyValues(keyValues: KeyValue[]): string {
    return keyValues.map((kv) => `${kv.key}=${kv.value}`).join(':');
  }

  private base64Encode(input: string): string {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(input);
      const base64 = btoa(String.fromCharCode(...data));
      // Make it URL-safe by replacing characters
      return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    } catch (error) {
      // Fallback to btoa for compatibility
      return btoa(input).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }
  }

  private generateConsentParamsMap(userConsent: UserConsent): Record<string, string> {
    return {
      gdpr_consent: userConsent?.gdpr?.consentString ?? '',
      gdpr: userConsent?.gdpr?.gdprApplies ? '1' : '0',
      us_privacy: userConsent?.uspConsent ?? '',
      gpp: userConsent?.gpp?.gppString ?? '',
      gpp_sid:
        userConsent.gpp && Array.isArray(userConsent.gpp.applicableSections)
          ? userConsent.gpp.applicableSections.join(',')
          : '',
    };
  }

  private makePostRequest(url: string, payload?: any): void {
    logger.info(`Making HTTP POST analytics request to ${url}`);
    navigator.sendBeacon(url, payload ? JSON.stringify(payload) : undefined);
  }

  private makeGetRequest(url: string, payload: any = undefined): void {
    let urlWithOptPayload: string = url;
    if (payload) {
      let payloadBase64: string;
      try {
        const encoder: TextEncoder = new TextEncoder();
        const payloadBytes: Uint8Array = encoder.encode(JSON.stringify(payload));
        payloadBase64 = btoa(String.fromCharCode(...payloadBytes));
      } catch (error) {
        payloadBase64 = btoa(JSON.stringify(payload));
      }
      urlWithOptPayload = `${url}&data=${payloadBase64}`;
    }
    logger.info(`Making HTTP GET analytics request to ${urlWithOptPayload}`);
    new Image().src = urlWithOptPayload;
  }

  private generateCommonPayloadProperties(uid?: string): Record<string, any> {
    const globalFacts: Facts = GlobalFactStore.getInstance().getFacts();
    const payload: any = {
      pid: this.propertyId,
      ref: globalFacts['page.url'] ?? '',
      br: globalFacts['browser.name'] ?? '',
      bl: globalFacts['browser.lang'] ?? '',
      bw: globalFacts['browser.width'] ?? 0,
      bh: globalFacts['browser.height'] ?? 0,
      dt: globalFacts['device.type'] ?? '',
      os: globalFacts['device.os'] ?? '',
      to: globalFacts['time.offset'],
      cy: globalFacts['geo.city'] ?? '',
      ct: globalFacts['geo.country'] ?? '',
      pc: globalFacts['geo.postcode'] ?? '',
      dl: `${Library.name}:${Library.version}`,
    };
    const publisherCohorts: String[] = globalFacts['publisher.cohort.ids'];
    if (publisherCohorts && publisherCohorts.length > 0) {
      payload.pcids = publisherCohorts;
    }
    if (uid) {
      payload.uid = uid;
    }
    return payload;
  }

  private generateOpportunityPayload(pvid: string, sid: string, uid?: string): Record<string, any> {
    const globalFacts: Facts = GlobalFactStore.getInstance().getFacts();
    const payload: any = {
      ...this.generateCommonPayloadProperties(uid),
      num: this.auctionNumber,
      pvid: pvid,
      sid: sid,
      td: globalFacts['time.day'],
      th: globalFacts['time.hour'],
      tm: globalFacts['time.minutes'],
      plcmts: [],
    };
    return payload;
  }

  private generatePageViewPayload(id: string, sid: string, uid?: string): Record<string, any> {
    const payload: any = {
      ...this.generateCommonPayloadProperties(uid),
      id: id,
      sid: sid,
    };
    return payload;
  }

  private buildBaseEventParameters(
    data: BaseEventAnalyticsData,
    userConsent: UserConsent,
    additionalParams: Record<string, any> = {}
  ): Record<string, string> {
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.GET
    );

    Object.assign(parameterMap, {
      rid: data.opportunityId,
      did: data.deliveryId,
      eai: data.advertiserId,
      eadms: data.advertiserDomains,
      gad: data.adUnit,
      lid: data.lineItemId,
      dlid: data.nodalsDealId,
      pvid: data.pvid,
      sid: data.sid,
      uid: data.uid,
      ...additionalParams,
    });

    return parameterMap;
  }

  private buildGenericEventParameters(
    data: GenericEventAnalyticsData,
    additionalParams: Record<string, any> = {}
  ): Record<string, string> {
    return this.buildBaseEventParameters(data, data.userConsent, {
      id: data.impressionId,
      ...additionalParams,
    });
  }

  private buildImpressionParameters(
    data: ImpressionAnalyticsData & { userConsent: UserConsent }
  ): Record<string, string> {
    const parameterMap = this.buildBaseEventParameters(data, data.userConsent, {
      id: data.id,
      crw: data.renderedData?.size?.width,
      crh: data.renderedData?.size?.height,
      crid: data.creativeId,
    });

    if (data.kvs && data.kvs.length > 0) {
      parameterMap.kvs = this.serializeKeyValues(data.kvs);
    }

    return parameterMap;
  }

  private handleGenericEvent<T extends { userConsent: UserConsent }>(
    eventData: T,
    eventType: EventType,
    logMessage: string,
    parameterBuilder: (data: T) => Record<string, string>,
    requestMethod: 'GET' | 'POST' = 'GET'
  ): void {
    logger.info(logMessage);
    const endpointUrl: string = this.getEndpointUrl(eventType);
    const parameterMap = parameterBuilder(eventData);

    const querystring: string = new URLSearchParams(
      Object.entries(parameterMap).filter(([_, value]) => value != null)
    ).toString();

    if (requestMethod === 'POST') {
      this.makePostRequest(`${endpointUrl}?${querystring}`);
    } else {
      this.makeGetRequest(`${endpointUrl}?${querystring}`);
    }
  }
}

export type { ReportingEvents } from './events';
export { AnalyticsRecorder };
